import { useTranslation } from 'react-i18next';
import Modal from '@/components/Modal/Modal';
import Button from '@/components/Button';
import { useMemo, useState } from 'react';
import { Country, InvoiceDocument } from '@/core/types';
import { ResolveErrorModalSkeleton } from '@/components/ResolveErrorModalSkeleton/ResolveErrorModalSkeleton';
import BackIcon from '@/icons/arrow-left.svg?react';
import ArrowRightIcon from '@/icons/arrow-right.svg?react';
import CloseIcon from '@/icons/close-circle.svg?react';
import IncludedToFilingIcon from '@/icons/included-to-filing.svg?react';
import MiniLoader from '@/components/MiniLoader/MiniLoader';
import { capitalize, formatDate, formatNumber } from '@/core/helpers';
import { normalizeDateFE } from '@/core/normalization';
import { useQuery } from '@tanstack/react-query';
import { getCountries } from '@/core/api';
import { INVOICE_UNCLEAR_KEY } from '../../pages/Seller/SellerDocuments/SellerDocuments.helpers';
import { NormalizedInvoiceErrors } from '../../pages/Seller/SellerDocuments/EditInvoiceModal';
import { InvoiceDocumentErrorSeverityEnum } from '@/core/enums';
import { ErrorBlock } from './ErrorBlock';
import Tooltip from '../Tooltip/Tooltip';

interface Props {
  isModalOpened: boolean;
  closeModal: () => void;
  invoice: InvoiceDocument | undefined;
  onSwitchModal: () => void;
  onConfirm: (
    setIsConfirming: (v: boolean) => void,
    setIsConfirmPartShown: (v: boolean) => void
  ) => () => void;
  onClickErrorIsUnclear: (v: string) => void;
  errorFeedbacks: string[] | undefined;
}

export function MuteDocumentModal({
  isModalOpened,
  closeModal,
  invoice,
  onSwitchModal,
  onConfirm,
  onClickErrorIsUnclear,
  errorFeedbacks = [],
}: Props) {
  const { t, i18n } = useTranslation();

  const { data: countries, isLoading: isLoadingCountries } = useQuery({
    queryKey: ['Countries'],
    queryFn: getCountries,
  });

  const countriesMap = useMemo(() => {
    return (countries || []).reduce(
      (acc: { [key: string]: Country }, item: Country) => {
        acc[item.short_name] = item;
        return acc;
      },
      {}
    );
  }, [countries]);

  const [isConfirming, setIsConfirming] = useState(false);
  const [isConfirmPartShown, setIsConfirmPartShown] = useState(false);

  const invoiceErrorsArr = invoice?.errors;

  const isInvoiceWithWarningsOnly = invoice?.errors?.every(
    (e) => e.severity === InvoiceDocumentErrorSeverityEnum.WARNING
  );

  const errorsInfo: NormalizedInvoiceErrors | undefined = useMemo(
    () =>
      (invoiceErrorsArr || []).reduce<NormalizedInvoiceErrors>(
        (acc, err) => {
          if (err.severity === InvoiceDocumentErrorSeverityEnum.WARNING) {
            acc.warningsCount++;
            acc.warningCodes.push(err.code);
          } else if (err.severity === InvoiceDocumentErrorSeverityEnum.ERROR) {
            acc.errorsCount++;
            acc.errorCodes.push(err.code);
          }
          return acc;
        },
        { errorsCount: 0, errorCodes: [], warningsCount: 0, warningCodes: [] }
      ),
    [invoiceErrorsArr]
  );

  const isInvoiceUnclearFeedbackSent =
    errorFeedbacks.includes(INVOICE_UNCLEAR_KEY);

  const onClickCloseModal = () => {
    setIsConfirmPartShown(false);
    closeModal();
  };

  const onClickInvoiceUnclear = () =>
    onClickErrorIsUnclear(INVOICE_UNCLEAR_KEY);

  const isLoading = isConfirming || isLoadingCountries;

  return (
    <Modal
      isOpened={isModalOpened}
      close={onClickCloseModal}
      title={t('Document')}
      isLoading={isLoading}
      name={'mute-document'}
      className='[&>div]:max-w-[1000px]'
      scrollable={false}
    >
      {invoice && (
        <>
          {isConfirming || isLoadingCountries ? (
            <ResolveErrorModalSkeleton />
          ) : (
            <div className='flex h-full max-h-[88vh] gap-x-12'>
              {isConfirmPartShown ? (
                <div className='flex flex-col items-stretch justify-between pb-6 pl-8 pt-8'>
                  <div>
                    <Button
                      data-testid={'suggestion-back-btn'}
                      styling='naked'
                      onClick={() => setIsConfirmPartShown(false)}
                      className='group mb-2 flex cursor-pointer items-center text-sm !font-semibold transition hover:text-green'
                    >
                      <BackIcon className='mr-1 group-hover:[&>g>path]:fill-green' />
                      <span>{t('Back')}</span>
                    </Button>
                    <h4 className='Title--sm pb-2 font-extrabold'>
                      {t(
                        'Invoice data in my invoicing system matches the Portal data'
                      )}
                    </h4>
                    <p>
                      {t(
                        'In this case, please follow these steps to ensure compliance with EU invoicing rules'
                      )}
                      :
                    </p>
                    <ol className='ml-4 list-decimal pb-4 font-semibold'>
                      <li>
                        {t(
                          'Create a storno of the incorrect invoice directly in your invoicing system, with the current date'
                        )}
                      </li>
                      <li>
                        {t(
                          'Create a new corrective invoice with the correct values, also with the current date'
                        )}
                      </li>
                    </ol>
                    <p>
                      {t(
                        (invoice?.errors || []).some(
                          (e) =>
                            e.severity ===
                            InvoiceDocumentErrorSeverityEnum.ERROR
                        )
                          ? "The affected (incorrect) invoice will not be included in this month's filing, and your new corrective invoice will be filed in the next month's submission."
                          : "The affected (incorrect) invoice will be included in this month's filing, and your new corrective invoice and storno will be filed in the next month's submission."
                      )}
                    </p>
                  </div>
                  <div className='space-y-6'>
                    <p>
                      <span className='font-semibold'>{t('Confirm_full')}</span>
                      <span>
                        {t(
                          'that you will create both the storno and corrective invoices in your system with the current date'
                        )}
                      </span>{' '}
                      <span>
                        {t(
                          'After confirming, the affected invoice will be marked as ”To be corrected” for this month'
                        )}
                      </span>
                    </p>
                    <div className='flex flex-wrap items-center justify-between gap-x-4 gap-y-4'>
                      <div className='flex shrink-0 items-center gap-x-4 gap-y-4'>
                        <Button
                          className='flex items-center'
                          disabled={isLoading}
                          onClick={onConfirm(
                            setIsConfirming,
                            setIsConfirmPartShown
                          )}
                          styling='red'
                          data-testid='confirm_mute_document_btn'
                        >
                          {isConfirming && isLoadingCountries && (
                            <MiniLoader className='mr-2' color='gray' />
                          )}
                          <span>{t('Confirm')}</span>
                        </Button>
                        <Button
                          styling='outline'
                          onClick={onClickCloseModal}
                          disabled={isLoading}
                        >
                          {t("I'll do this later")}
                        </Button>
                      </div>
                      {isInvoiceUnclearFeedbackSent ? (
                        <Tooltip
                          content={t(
                            'It will help us to make our proccess more clear and transparent.'
                          )}
                        >
                          <div
                            className='text-gray-700'
                            data-testid='invoice_unclear_text'
                          >
                            {t('Thank you for your feedback')}
                          </div>
                        </Tooltip>
                      ) : (
                        <Button
                          styling='outline'
                          onClick={onClickInvoiceUnclear}
                          disabled={isLoading}
                          data-testid='invoice_unclear_btn'
                        >
                          {t('Not clear to me')}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className='pb-6 pl-8 pt-8'>
                  <h4 className='Title--sm pb-2 font-extrabold'>
                    {isInvoiceWithWarningsOnly
                      ? t(
                          'We found notices in the invoice data on the Portal. Invoice will be included in the filing, but we recommend to make corrections'
                        )
                      : t(
                          'We found errors in the invoice data on the Portal that prevent us from including it to the filings'
                        )}
                  </h4>
                  <div className='pb-6'>
                    <span
                      dangerouslySetInnerHTML={{
                        __html: `<span>${t(
                          'Please open your invoicing system and find the invoice *invoiceNumber**date*'
                        )
                          .replace(
                            '*invoiceNumber*',
                            invoice.invoice_number
                              ? `<span class='font-semibold'>${invoice.invoice_number}</span>`
                              : ''
                          )
                          .replace(
                            '*date*',
                            invoice.document_date
                              ? ` <span class='font-semibold'>(${
                                  i18n.language === 'de' ? 'vom' : 'dated'
                                } ${formatDate(
                                  normalizeDateFE(invoice.document_date),
                                  'dd.MM.yyyy'
                                )})</span>`
                              : ''
                          )}</span>`,
                      }}
                    />{' '}
                    <span>
                      {t(
                        'Compare the values to those in the Portal, ensuring that all amounts and details match exactly to avoid discrepancies'
                      )}
                    </span>
                  </div>
                  <p className='pb-2 font-semibold'>
                    {t('After comparing, choose one of the options below')}:
                  </p>
                  <div className='space-y-4'>
                    {[
                      {
                        id: 'invoice_data_matches',
                        title:
                          'Invoice data in my invoicing system matches the Portal data',
                        description:
                          "In this case, you'll need to issue a storno of the incorrect invoice and generate a new corrective invoice with the current date",
                        action: () => {
                          setIsConfirmPartShown(true);
                        },
                        classNames: '!p-4',
                      },
                      {
                        id: 'invoice_data_differs',
                        title:
                          'Invoice data in my invoicing system differs from the Portal data',
                        description:
                          "You'll need to update the information in the Portal to match your invoice",
                        action: () => onSwitchModal(),
                        classNames: '!p-4',
                      },
                      {
                        id: INVOICE_UNCLEAR_KEY,
                        title:
                          'It is not clear for me how to resolve this invoice',
                        action: onClickInvoiceUnclear,
                        className: '!py-2 !px-4',
                      },
                    ]
                      .filter(
                        (o) =>
                          !isInvoiceUnclearFeedbackSent ||
                          o.id !== INVOICE_UNCLEAR_KEY
                      )
                      .map((o) => (
                        <Button
                          styling='outline'
                          className={`flex !w-full items-center justify-between gap-x-8 rounded-lg border border-gray-500 text-start ${o.className}`}
                          key={o.title}
                          onClick={o.action}
                          data-testid={`mute-option-${o.title}`}
                        >
                          <div>
                            <div className='font-semibold'>{t(o.title)}</div>
                            {o.description && (
                              <p className='font-normal text-gray-700'>
                                {t(o.description)}
                              </p>
                            )}
                          </div>
                          <div>
                            <ArrowRightIcon className='[&>path]:fill-dark' />
                          </div>
                        </Button>
                      ))}
                    {isInvoiceUnclearFeedbackSent && (
                      <div
                        className='px-4 py-2 text-gray-700'
                        data-testid='mute-invoice-modal-invoiceUnclearFeedbackSent'
                      >
                        {t(
                          'Thank you for your feedback! It will help us to make our proccess more clear and transparent.'
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
              {
                <div className='MiniScrollbar max-w-[442px] flex-shrink-0 overflow-y-auto bg-gray-70 pb-5 pl-6 pr-10 pt-8'>
                  <div className='text-h4 font-bold'>
                    {!!invoice?.invoice_number
                      ? invoice.invoice_number
                      : t('Document')}
                  </div>
                  <div className='pb-4'>
                    {invoice?.type ? (
                      <span>{`${capitalize(
                        invoice.type?.toLowerCase()
                      )} • `}</span>
                    ) : null}
                    {invoice?.document_date ? (
                      <span>{`${formatDate(
                        normalizeDateFE(invoice.document_date),
                        'dd.MM.yyyy'
                      )} • `}</span>
                    ) : null}
                    <div className='inline-flex items-center gap-x-1'>
                      {invoice?.errors.filter(
                        (e) =>
                          e.severity === InvoiceDocumentErrorSeverityEnum.ERROR
                      ).length ? (
                        <>
                          <span>{t('Not included to filing')}</span>
                          <CloseIcon />
                        </>
                      ) : (
                        <>
                          <span>{t('Included to filing')}</span>
                          <IncludedToFilingIcon className='h-4 w-4' />
                        </>
                      )}
                    </div>
                  </div>
                  <div className='space-y-2'>
                    <div className='flex items-start gap-x-1'>
                      <div className='w-1/2 text-gray-700'>{t('Taxed at')}</div>
                      <div className='w-1/2'>
                        {invoice?.vat_country &&
                          `${invoice.vat_country} • ${t(
                            invoice.vat_country as unknown as string
                          )} • `}
                        {invoice?.is_oss ? 'OSS' : 'Local'}
                      </div>
                    </div>
                    <div className='flex items-start gap-x-1'>
                      <div className='w-1/2 text-gray-700'>
                        {t('Buyer VAT number')}
                      </div>
                      <div className='w-1/2'>
                        {invoice?.b2b_buyer_vat_number}
                      </div>
                    </div>
                    <div className='flex items-start gap-x-1'>
                      <div className='w-1/2 text-gray-700'>
                        {t('Departure country_full')}
                      </div>
                      <div className='w-1/2'>
                        {invoice?.departure_country as string}:{' '}
                        {
                          countriesMap[invoice?.departure_country as string]
                            ?.name
                        }
                      </div>
                    </div>
                    <div className='flex items-start gap-x-1'>
                      <div className='w-1/2 text-gray-700'>
                        {t('Arrival country_full')}
                      </div>
                      <div className='w-1/2'>
                        {invoice?.arrival_country as string}:{' '}
                        {
                          countriesMap[invoice?.departure_country as string]
                            ?.name
                        }
                      </div>
                    </div>
                    <div className='flex items-start gap-x-1'>
                      <div className='w-1/2 text-gray-700'>
                        {t('Brutto amount')}
                      </div>
                      <div className='w-1/2' data-testid='brutto_amount'>
                        {formatNumber((invoice?.brutto_amount || 0) / 100, {
                          currency: invoice?.currency,
                          maximumFractionDigits: 2,
                        })}
                      </div>
                    </div>
                    <div className='flex items-start gap-x-1'>
                      <div className='w-1/2 text-gray-700'>
                        {t('Netto amount')}
                      </div>
                      <div className='w-1/2' data-testid='net_amount'>
                        {formatNumber((invoice?.net_amount || 0) / 100, {
                          currency: invoice?.currency,
                          maximumFractionDigits: 2,
                        })}
                      </div>
                    </div>
                    <div className='flex items-start gap-x-1 pb-6'>
                      <div className='w-1/2 text-gray-700'>
                        {t('VAT amount')}
                      </div>
                      <div className='w-1/2' data-testid='vat_amount'>
                        {formatNumber((invoice?.vat_amount || 0) / 100, {
                          currency: invoice?.currency,
                          maximumFractionDigits: 2,
                        })}
                      </div>
                    </div>
                  </div>
                  <div>
                    {!!errorsInfo?.errorsCount && (
                      <div>
                        <div className='font-semibold text-red-500'>
                          {errorsInfo.errorCodes.length} {t('Errors')}:
                        </div>
                        <div className='divide-y rounded-md border bg-gray-400'>
                          {errorsInfo.errorCodes
                            .filter((e, i, self) => self.indexOf(e) === i)
                            .map((errorName) => (
                              <ErrorBlock
                                errorName={errorName}
                                key={errorName}
                                onClickErrorIsUnclear={() => {
                                  if (!invoice) return;
                                  onClickErrorIsUnclear(errorName);
                                }}
                                isFeedbackSent={errorFeedbacks.includes(
                                  errorName
                                )}
                              />
                            ))}
                        </div>
                      </div>
                    )}
                    {!!errorsInfo?.warningsCount && (
                      <div>
                        <div className='font-semibold text-orange-500'>
                          {errorsInfo.warningCodes.length} {t('Notices')}:
                        </div>
                        <div className='divide-y rounded-md border bg-gray-400'>
                          {errorsInfo.warningCodes
                            .filter((e, i, self) => self.indexOf(e) === i)
                            .map((errorName) => (
                              <ErrorBlock
                                errorName={errorName}
                                key={errorName}
                                onClickErrorIsUnclear={() => {
                                  if (!invoice) return;
                                  onClickErrorIsUnclear(errorName);
                                }}
                                isFeedbackSent={errorFeedbacks.includes(
                                  errorName
                                )}
                              />
                            ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              }
            </div>
          )}
        </>
      )}
    </Modal>
  );
}
